import React, { useRef, useState, useCallback } from "react";
import Papa from "papaparse";
import { Nav, NavItem, NavLink, TabContent } from "reactstrap";
import classnames from "classnames";

import { useProfile } from "Components/Hooks/UserHooks";
import { importLTA } from "common/services/ltas.service";

import {
  FieldMapping,
  ImportResults,
  ImportStatus,
  MAX_IMPORT_RECORDS,
  autoMapFields,
  mapRowToRecord,
} from "./ltaImportFields";
import LTAsImportWizardTabFileUpload from "./LTAsImportWizardTabFileUpload";
import LTAsImportWizardTabFieldMapping from "./LTAsImportWizardTabFieldMapping";
import LTAsImportWizardTabDataPreview from "./LTAsImportWizardTabDataPreview";
import LTAsImportWizardTabFinish from "./LTAsImportWizardTabFinish";

const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;

type ImportFn = typeof importLTA;

const LTAsImportWizard: React.FC<{ importFn?: ImportFn }> = ({
  importFn = importLTA,
}) => {
  const { token } = useProfile();

  const [activeTab, setActiveTab] = useState(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([1]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [firstLineIncludesFieldNames, setFirstLineIncludesFieldNames] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);

  const fileReady = !!selectedFile && csvRows.length > 0 && !csvParseError;

  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [importStatus, setImportStatus] = useState<ImportStatus>("idle");
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const steps = [
    { tab: 1, label: "File Upload",   icon: "ri-upload-cloud-2-line", locked: false },
    { tab: 2, label: "Field Mapping", icon: "ri-git-merge-line",       locked: !fileReady },
    { tab: 3, label: "Data Preview",  icon: "ri-eye-line",             locked: !fileReady },
    { tab: 4, label: "Import",        icon: "ri-upload-2-line",        locked: !fileReady },
  ];

  function toggleTab(tab: number) {
    if (activeTab === tab) return;
    if (tab < 1 || tab > 4) return;
    if (tab > 1 && !fileReady) return;
    setActiveTab(tab);
    setPassedSteps((prev) => [...prev, tab]);
  }

  const formatFileSize = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(2)} KB` : `${(kb / 1024).toFixed(2)} MB`;
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handlePickedFiles = (files: FileList | File[]) => {
    const file = Array.isArray(files) ? files[0] : files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setCsvParseError(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setFieldMappings({});
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length && results.data.length === 0) {
          setCsvParseError("Could not parse file. Please check the format.");
          return;
        }
        const headers = results.meta.fields ?? [];
        setCsvHeaders(headers);
        setCsvRows(results.data);
        setFieldMappings(autoMapFields(headers));
      },
      error: (err) => setCsvParseError(err.message),
    });
  };

  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const handleStartOver = () => {
    stopProgressSimulation();
    setSelectedFile(null);
    setIsDragActive(false);
    setFirstLineIncludesFieldNames(true);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvParseError(null);
    setFieldMappings({});
    setImportStatus("idle");
    setImportProgress({ current: 0, total: 0 });
    setImportResults(null);
    setImportErrors([]);
    setActiveTab(1);
    setPassedSteps([1]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const runImport = async () => {
    const rowsToImport = csvRows.slice(0, MAX_IMPORT_RECORDS);
    const total = rowsToImport.length;
    setImportStatus("running");
    setImportErrors([]);
    setImportProgress({ current: 0, total });

    let simPct = 0;
    progressIntervalRef.current = setInterval(() => {
      simPct = simPct + (90 - simPct) * 0.07;
      setImportProgress({ current: Math.round((simPct / 100) * total), total });
    }, 400);

    try {
      const records = rowsToImport.map((row) => mapRowToRecord(row, fieldMappings));
      const result = await importFn({ data: records }, token, { timeoutMs: IMPORT_TIMEOUT_MS });
      const successful: number = result.metadata?.data?.successful ?? 0;
      const failed: number = result.metadata?.data?.failed ?? 0;
      const downloadUrl = failed > 0 && result.blob && result.blob.size > 0
        ? URL.createObjectURL(result.blob) : null;
      setImportResults({ total, successful, failed, downloadUrl });
    } catch (error: any) {
      const isTimeout = error?.name === "AbortError";
      const msg = isTimeout
        ? "The import request timed out. Try reducing the number of rows or increasing the server timeout."
        : (error?.response?.data?.error?.message ?? error?.message ?? "Import request failed.");
      setImportErrors([msg]);
      setImportResults({ total, successful: 0, failed: total, downloadUrl: null });
    } finally {
      stopProgressSimulation();
      setImportProgress({ current: total, total });
      setImportStatus("done");
    }
  };

  return (
    <>
      <div className="step-arrow-nav mb-4">
        <Nav className="nav-pills nav-justified custom-nav" role="tablist">
          {steps.map(({ tab, label, icon, locked }) => (
            <NavItem key={tab} role="presentation">
              <NavLink
                href="#"
                className={classnames(
                  { active: activeTab === tab, done: activeTab > tab },
                  "p-3 fs-15 d-flex align-items-center justify-content-center gap-2",
                  { "text-muted": locked }
                )}
                style={locked ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                title={locked ? "Complete the previous step to unlock" : label}
                onClick={() => toggleTab(tab)}
              >
                <span
                  className={classnames(
                    "d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0",
                    locked ? "bg-light text-muted" : activeTab > tab ? "bg-success-subtle text-success" : "bg-primary-subtle text-primary"
                  )}
                  style={{ width: 30, height: 30, fontSize: 15 }}
                >
                  {activeTab > tab ? <i className="ri-check-line" /> : locked ? <i className="ri-lock-line" /> : <i className={icon} />}
                </span>
                <span className="d-none d-sm-inline">{label}</span>
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={activeTab} className="pt-3">
        <LTAsImportWizardTabFileUpload
          activeTab={activeTab} toggleTab={toggleTab}
          selectedFile={selectedFile} isDragActive={isDragActive}
          setIsDragActive={setIsDragActive} fileInputRef={fileInputRef}
          openFilePicker={openFilePicker} handlePickedFiles={handlePickedFiles}
          firstLineIncludesFieldNames={firstLineIncludesFieldNames}
          setFirstLineIncludesFieldNames={setFirstLineIncludesFieldNames}
          formatFileSize={formatFileSize} onStartOver={handleStartOver}
          csvParseError={csvParseError} csvRowCount={csvRows.length}
        />
        <LTAsImportWizardTabFieldMapping
          activeTab={activeTab} toggleTab={toggleTab}
          csvHeaders={csvHeaders} fieldMappings={fieldMappings}
          setFieldMappings={setFieldMappings}
        />
        <LTAsImportWizardTabDataPreview
          activeTab={activeTab} toggleTab={toggleTab}
          csvHeaders={csvHeaders} csvRows={csvRows} fieldMappings={fieldMappings}
        />
        <LTAsImportWizardTabFinish
          csvRows={csvRows} fieldMappings={fieldMappings}
          importStatus={importStatus} importProgress={importProgress}
          importResults={importResults} importErrors={importErrors}
          onRunImport={runImport} onStartOver={handleStartOver}
        />
      </TabContent>
    </>
  );
};

export default LTAsImportWizard;
